import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import { StyledCard } from "../common/StyledCard";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InfoRow from "../common/InfoRow";
import { PaymentMethodResponseDto } from "../../shared/dtos/paymentMethod/PaymentMethodResponseDto";
import { PaymentMethodType } from "../../shared/enums/PaymentMethodType";

interface PaymentMethodCardProps {
  method: PaymentMethodResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const getTypeColor = (
  type: PaymentMethodType,
): "success" | "primary" | "secondary" | "default" => {
  switch (type) {
    case "CASH":
      return "success";
    case "MOBILE_MONEY":
      return "primary";
    case "BANK_TRANSFER":
      return "secondary";
    default:
      return "default";
  }
};

const PaymentMethodCard: React.FC<PaymentMethodCardProps> = ({
  method,
  onEdit,
  onDelete,
}) => {
  return (
    <StyledCard>
      <Box
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }} noWrap>
              {method.name}
            </Typography>
            <Stack sx={{ flexDirection: "row", spacing: 1, mt: 0.5, gap: 1 }}>
              <Chip
                label={method.type.replace("_", " ")}
                size="small"
                color={getTypeColor(method.type)}
                sx={{ height: 20, fontSize: "0.7rem", fontWeight: "bold" }}
              />
              <Chip
                label={method.active ? "Active" : "Inactive"}
                size="small"
                variant="outlined"
                color={method.active ? "success" : "default"}
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Stack>
          </Box>
          <Stack sx={{ flexDirection: "row" }}>
            {onEdit && (
              <Tooltip title="Edit">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Details Body */}
        <Stack sx={{ spacing: 2, flexGrow: 1, gap: 2 }}>
          <InfoRow
            icon={<AccountBalanceIcon fontSize="small" color="action" />}
            label="Linked Account"
            value={method.accountName || "Not Linked"}
          />
          <InfoRow
            icon={<PaymentsIcon fontSize="small" color="action" />}
            label="Payment Type"
            value={method.type}
          />
        </Stack>
      </Box>
    </StyledCard>
  );
};

export default PaymentMethodCard;
